import React from "react";
import { Link } from "react-router-dom";
import { FiEdit2, FiTrash2 } from "react-icons/fi";

/**
 * DataTable - Reusable table with sticky headers and row actions
 *
 * @param {array} columns - Column definitions [{key, header, width?, render?}]
 * @param {array} data - Array of row data objects
 * @param {function} onEdit - Callback when edit action clicked (receives row)
 * @param {function} onDelete - Callback when delete action clicked (receives row)
 * @param {string} editLinkBase - Base URL for edit links (e.g., "/clients")
 * @param {string} idKey - Key to use for row ID (default: "id")
 * @param {string} emptyMessage - Message when no data
 * @param {boolean} loading - Show loading state
 * @param {function} rowHighlight - Function to determine if row should be highlighted (receives row)
 * @param {string} variant - Style variant: "default" | "v4" (card-based rows)
 * @param {function} onRowClick - Callback when row is clicked (receives row)
 * @param {any} selectedId - ID of currently selected row (for highlighting)
 */
const DataTable = ({
	columns = [],
	data = [],
	onEdit,
	onDelete,
	editLinkBase,
	idKey = "id",
	emptyMessage = "No data available",
	loading = false,
	rowHighlight,
	variant = "default",
	onRowClick,
	selectedId,
}) => {
	// Determine if we should show actions column
	const showActions = onEdit || onDelete || editLinkBase;

	const tableClass = `data-table ${variant === "v4" ? "data-table--v4" : ""}`;

	if (loading) {
		return (
			<div className={tableClass}>
				<div className="data-table__loading">Loading...</div>
			</div>
		);
	}

	if (data.length === 0) {
		return (
			<div className={tableClass}>
				<div className="data-table__empty">{emptyMessage}</div>
			</div>
		);
	}

	return (
		<div className="data-table__wrapper">
			<div className={tableClass}>
				{/* Header */}
				<div className="data-table__header">
					<div className="data-table__row">
						{columns.map((col) => (
							<div
								key={col.key}
								className={`data-table__cell ${col.width ? `data-table__cell--${col.width}` : ""}`}
							>
								{col.header}
							</div>
						))}
						{showActions && (
							<div className="data-table__cell data-table__cell--actions">
								Actions
							</div>
						)}
					</div>
				</div>

				{/* Body */}
				<div className="data-table__body">
					{data.map((row, rowIndex) => {
						const rowId = row[idKey] || rowIndex;
						const isSelected = selectedId !== undefined && rowId === selectedId;
						const isHighlighted = rowHighlight && rowHighlight(row);
						const isClickable = !!onRowClick;

						return (
						<div
							className={`data-table__row ${isHighlighted ? "data-table__row--highlight" : ""} ${isSelected ? "data-table__row--selected" : ""} ${isClickable ? "data-table__row--clickable" : ""}`}
							key={rowId}
							onClick={onRowClick ? (e) => {
								// Don't trigger row click if clicking on a link or button
								if (e.target.closest('a, button')) return;
								onRowClick(row);
							} : undefined}
						>
							{columns.map((col) => (
								<div
									key={col.key}
									className={`data-table__cell ${col.width ? `data-table__cell--${col.width}` : ""}`}
								>
									{col.render ? col.render(row[col.key], row) : row[col.key]}
								</div>
							))}
							{showActions && (
								<div className="data-table__cell data-table__cell--actions">
									{editLinkBase ? (
										<Link
											to={`${editLinkBase}/${row[idKey]}`}
											className="data-table__action"
											title="Edit"
										>
											<FiEdit2 />
										</Link>
									) : onEdit ? (
										<button
											className="data-table__action"
											onClick={() => onEdit(row)}
											title="Edit"
										>
											<FiEdit2 />
										</button>
									) : null}

									{onDelete && (
										<button
											className="data-table__action data-table__action--danger"
											onClick={() => onDelete(row)}
											title="Delete"
										>
											<FiTrash2 />
										</button>
									)}
								</div>
							)}
						</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

/**
 * Helper component for status badges
 */
export const StatusBadge = ({ status }) => {
	const statusClass = status ? status.toLowerCase().replace(/\s+/g, "-") : "draft";
	return (
		<span className={`status-badge status-badge--${statusClass}`}>
			{status || "Draft"}
		</span>
	);
};

/**
 * Helper to format dates consistently
 */
export const formatDate = (dateString) => {
	if (!dateString) return "—";
	const date = new Date(dateString);
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
};

/**
 * Helper to format currency
 */
export const formatCurrency = (amount) => {
	if (amount === null || amount === undefined) return "—";
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
	}).format(amount);
};

export default DataTable;
